import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { endpoint } from "./api.services";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";

// Without this, axios resolves every relative endpoint against the admin panel's
// own origin, so requests hit the static host (405) instead of the backend.
if (!import.meta.env.VITE_API) {
  throw new Error(
    "VITE_API is not set. The app was built without an API base URL — " +
      "add it to .env (see .env.example) and rebuild."
  );
}

const baseURL: string = import.meta.env.VITE_API;
const axiosInstance = axios.create({
  baseURL: baseURL,
  timeout: Number(import.meta.env.VITE_API_REQUEST_TIMEOUT) || 30000,
});

// let isRefreshing = false;
// let failedQueue: any[] = [];

// const processQueue = (error: any, token: string | null = null) => {
//   failedQueue.forEach((prom) => {
//     if (error) {
//       prom.reject(error);
//     } else {
//       prom.resolve(token);
//     }
//   });
//   failedQueue = [];
// };

// axiosInstance.interceptors.response.use(
//   (response) => response,
//   async (error: AxiosError) => {
//     const originalRequest = error.config as any;

//     if (error?.response?.status === 401 && !originalRequest._retry) {
//       const refresh =
//         Cookies.get("refresh") || sessionStorage.getItem("refresh");

//       if (!refresh) {
//         // Optional logout logic
//         return Promise.reject(error);
//       }

//       if (isRefreshing) {
//         // Queue the request until refresh is done
//         return new Promise((resolve, reject) => {
//           failedQueue.push({
//             resolve: (token: string) => {
//               originalRequest.headers.Authorization = `Bearer ${token}`;
//               resolve(axiosInstance(originalRequest));
//             },
//             reject,
//           });
//         });
//       }

//       originalRequest._retry = true;
//       isRefreshing = true;

//       try {
//         const response = await fetch(baseURL + endpoint["auth"]["refresh"], {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({ refresh }),
//         });

//         if (!response.ok) {
//           const errorData = await response.text();
//           throw new Error(`Refresh failed: ${errorData}`);
//         }

//         const { access } = await response.json();

//         if (!access) throw new Error("No access token received");

//         Cookies.set("access", access);

//         processQueue(null, access);
//         isRefreshing = false;

//         originalRequest.headers.Authorization = `Bearer ${access}`;
//         return axiosInstance(originalRequest);
//       } catch (err) {
//         processQueue(err, null);
//         isRefreshing = false;
//         // Optionally clear auth and redirect
//         Cookies.remove("access");
//         Cookies.remove("refresh");
//         // window.location.href = "/login";
//         return Promise.reject(err);
//       }
//     }
//     if (error?.response?.status <= 500) {
//       throwErrorAlert(error?.response?.data);
//     }
//     if (error?.response?.status === 500) {
//       toast.error("Internal Server Error", {
//         description: error?.message,
//         closeButton: true,
//         classNames: {
//           description: "!text-black",
//           closeButton: "!absolute !right-0",
//           icon: "!mr-10",
//         },
//       });
//       throw error;
//     }

//     if (axios.isCancel(error)) {
//       console.log("Request canceled", error.message);
//     }
//     // return Promise.reject(error.response);
//     return Promise.reject(error.response);
//   }
// );

// export default axiosInstance;

// REQUEST INTERCEPTOR - Attach access token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Only add token if Authorization header is not already set
    if (!config.headers.Authorization) {
      const accessToken =
        Cookies.get("access") || sessionStorage.getItem("access");

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global state for token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Process queued requests after refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Clear auth data and optionally redirect
const clearAuthAndRedirect = () => {
  Cookies.remove("access");
  Cookies.remove("refresh");
  sessionStorage.removeItem("refresh");
  sessionStorage.removeItem("access");

  // Uncomment if you want automatic redirect
  // window.location.href = "/login";
};

// Refresh token with timeout
const refreshToken = async (refreshToken: string): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

  try {
    const response = await fetch(baseURL + endpoint["auth"]["refresh"], {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh: refreshToken }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Refresh failed: ${response.status} - ${errorData}`);
    }

    const data = await response.json();

    if (!data.access) {
      throw new Error("No access token received");
    }

    return data.access;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

// RESPONSE INTERCEPTOR - Handle token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    console.log("THE TOAST WAS THROWN FROM IN HERE!", error);
    const originalRequest = error.config as any;
    // Handle 401 Unauthorized - Token refresh logic
    if (error?.response?.status === 401 && !originalRequest._retry) {
      const refresh =
        Cookies.get("refresh") || sessionStorage.getItem("refresh");

      if (!refresh) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: (err: any) => reject(err),
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshToken(refresh);

        // Store new token
        Cookies.set("access", newAccessToken, {
          secure: true,
          sameSite: "strict",
          expires: 7, // 7 days
        });

        // Process queued requests
        processQueue(null, newAccessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle specific client errors (400-499, excluding 401)
    if (
      error?.response?.status &&
      error.response.status >= 400 &&
      error.response.status < 500 &&
      error.response.status !== 401
    ) {
      throwErrorAlert(error?.response?.data);
    }

    // Handle server errors (500+)
    if (error?.response?.status && error.response.status >= 500) {
      showToast({
        title: "Internal Server Error",
        description: error?.message || "Something went wrong on our end",
        type: "error",
      });
    }

    // Handle network errors
    if (!error?.response) {
      showToast({
        title: "Network Error",
        description: "Please check your internet connection",
        type: "error",
      });
    }

    // Handle canceled requests
    if (axios.isCancel(error)) {
      console.log("Request canceled:", error.message);
      return Promise.reject(error);
    }

    // Return the full error for proper error handling upstream
    return Promise.reject(error);
  }
);

export default axiosInstance;
export { baseURL };
